import { InformationCircleIcon } from '@heroicons/react/16/solid';
import { XMarkIcon } from '@heroicons/react/20/solid';
import React, { PropsWithChildren, ReactNode } from 'react';
import { Position } from '../../hooks/usePositions';
import { ProductType } from '../../hooks/useProducts';
import { formatDollar, formatNumber } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { getDecimalPlaces } from '../../lib/math';
import { positionStopOrderTooltip } from '../../lib/tooltips';
import { OrderSide } from '../../types/generated';
import { Button } from '../catalyst-ui-kit/button';
import { Input } from '../Input/Input';
import { OraclePrice } from '../OraclePrice/OraclePrice';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { Tooltip } from '../Tooltip/Tooltip';
import { TpSlError } from '../TpSlError/TpSlError';
import { ProfitLossField } from './ProfitLossField';
import { StopOrderMessage } from './StopOrderMessage';

const Row = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-contentSecondary">{label}</span>
    <span className="text-contentPrimary">{value}</span>
  </div>
);

const Section: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="my-4 flex flex-col gap-2">{children}</div>
);

interface StopOrderState {
  price: bigint | undefined;
  pnl: bigint | undefined;
  pnlPercentage: bigint | undefined;
  customPercentage: bigint | undefined;
  hasError: boolean;
  hasExistingOrder: boolean;
  isFocused: boolean;
  setPrice: (price: bigint | undefined) => void;
  setCustomPercentage: (pct: bigint | undefined) => void;
  setIsFocused: (focused: boolean) => void;
}

export interface PositionStopOrderModalUiProps {
  isOpen: boolean;
  setPositionStopOrderModal: (newState: {
    positionId: string | undefined;
    openOrClosed: 'open' | 'closed';
  }) => void;
  position: Position;
  product: ProductType;
  onSubmitClick: () => void;
  takeProfit: StopOrderState;
  stopLoss: StopOrderState;
  OraclePriceComp: typeof OraclePrice;
  TpSlErrorComp: typeof TpSlError;
}

export const PositionStopOrderModalUi: React.FC<PositionStopOrderModalUiProps> = ({
  isOpen,
  setPositionStopOrderModal,
  position,
  product,
  onSubmitClick,
  takeProfit,
  stopLoss,
  OraclePriceComp,
  TpSlErrorComp,
}) => {
  const sizeDecimals = getDecimalPlaces(product.lotSize);
  const priceDecimals = getDecimalPlaces(product.tickSize);
  const hasStopOrder = takeProfit.hasExistingOrder || stopLoss.hasExistingOrder;

  return (
    <ResponsiveModal
      childrenContainerClassName="h-full w-[350px]"
      title={
        <span className="flex items-center gap-1">
          {hasStopOrder ? 'Modify' : 'Add'} TP/SL for Entire Position{' '}
          <Tooltip content={positionStopOrderTooltip()}>
            <InformationCircleIcon width={16} height={16} />
          </Tooltip>
        </span>
      }
      onClose={() => setPositionStopOrderModal({ positionId: undefined, openOrClosed: 'closed' })}
      open={isOpen}
    >
      <div className="flex flex-col gap-1">
        <Row label={'Market'} value={product.displayTicker} />
        <Row
          label={'Size'}
          value={`${formatNumber(Gwei.abs(position.size), { maximumFractionDigits: sizeDecimals, minimumFractionDigits: sizeDecimals })} ${product.baseTokenName}`}
        />
        <Row
          label={'Entry Price'}
          value={`${formatDollar(position.avgPrice, { maximumFractionDigits: priceDecimals, minimumFractionDigits: priceDecimals })}`}
        />
        <Row
          label={'Oracle Price'}
          value={<OraclePriceComp productId={product.id} priceDecimals={priceDecimals} />}
        />
      </div>
      <Section>
        <div className="flex items-end gap-1">
          <Input
            inputType="number"
            name="takeProfit"
            label="Take Profit"
            aria-describedby="takeProfit"
            aria-errormessage={takeProfit.hasError ? 'take-profit-error' : undefined}
            inputMode="numeric"
            step={Gwei.fmt(product.tickSize)}
            isAllowed={(values) => {
              const { floatValue = 0 } = values;
              return floatValue < Number(Gwei.fmt(product.maxPrice));
            }}
            min={Number(Gwei.fmt(product.minPrice))}
            decimalScale={priceDecimals}
            value={takeProfit.price !== undefined ? `${Gwei.fmt(takeProfit.price)}` : ''}
            onValueChange={(values, sourceInfo) => {
              if (sourceInfo.source !== 'event') return;
              if (values.value === '') {
                takeProfit.setPrice(undefined);
                takeProfit.setCustomPercentage(undefined);
                return;
              }
              takeProfit.setCustomPercentage(undefined);
              takeProfit.setPrice(Gwei.toGwei(values.value));
            }}
            onFocus={() => takeProfit.setIsFocused(true)}
            onBlur={() => takeProfit.setIsFocused(false)}
            rightElement={
              takeProfit.price !== undefined && (
                <Tooltip content={takeProfit.hasExistingOrder ? 'Remove Take Profit' : undefined}>
                  <Button
                    text
                    color="secondary"
                    aria-label="Remove"
                    onClick={() => {
                      takeProfit.setPrice(undefined);
                      takeProfit.setCustomPercentage(undefined);
                    }}
                  >
                    <XMarkIcon className="w-4" />
                  </Button>
                </Tooltip>
              )
            }
            containerClassName={takeProfit.hasError ? 'border border-borderError' : ''}
            hasError={takeProfit.hasError}
          />
          <ProfitLossField
            type="Gain"
            setPercentage={takeProfit.setCustomPercentage}
            setPrice={takeProfit.setPrice}
            position={position}
            product={product}
            pnlPercentage={takeProfit.pnlPercentage}
            hasError={takeProfit.hasError}
            customPercentage={takeProfit.customPercentage}
          />
        </div>
        <div className="h-8">
          <StopOrderMessage
            hasError={takeProfit.hasError}
            isInputFocused={takeProfit.isFocused}
            price={takeProfit.price}
            hasExistingOrder={takeProfit.hasExistingOrder}
            pnl={takeProfit.pnl}
            pnlPercentage={takeProfit.pnlPercentage}
            type="tp"
            priceDecimals={priceDecimals}
            stopLossPrice={stopLoss.price}
            takeProfitPrice={takeProfit.price}
            side={position.side === OrderSide.BUY ? 'long' : 'short'}
            productId={product.id}
            TpSlErrorComp={TpSlErrorComp}
          />
        </div>
      </Section>
      <Section>
        <div className="flex items-end gap-1">
          <Input
            name="stopLoss"
            label="Stop Loss"
            inputType="number"
            aria-describedby={'stopLoss'}
            aria-errormessage={stopLoss.hasError ? 'stop-loss-error' : undefined}
            inputMode="numeric"
            step={Gwei.fmt(product.tickSize)}
            isAllowed={(values) => {
              const { floatValue = 0 } = values;
              return floatValue < Number(Gwei.fmt(product.maxPrice));
            }}
            min={Number(Gwei.fmt(product.minPrice))}
            decimalScale={priceDecimals}
            value={stopLoss.price !== undefined ? `${Gwei.fmt(stopLoss.price)}` : ''}
            onValueChange={(values, sourceInfo) => {
              if (sourceInfo.source !== 'event') return;
              if (values.value === '') {
                stopLoss.setPrice(undefined);
                stopLoss.setCustomPercentage(undefined);
                return;
              }
              stopLoss.setCustomPercentage(undefined);
              stopLoss.setPrice(Gwei.toGwei(values.value));
            }}
            onFocus={() => stopLoss.setIsFocused(true)}
            onBlur={() => stopLoss.setIsFocused(false)}
            rightElement={
              stopLoss.price !== undefined && (
                <Tooltip content={stopLoss.hasExistingOrder ? 'Remove Stop Loss' : undefined}>
                  <Button
                    text
                    color="secondary"
                    aria-label="Remove"
                    onClick={() => {
                      stopLoss.setPrice(undefined);
                      stopLoss.setCustomPercentage(undefined);
                    }}
                  >
                    <XMarkIcon className="w-4" />
                  </Button>
                </Tooltip>
              )
            }
            containerClassName={stopLoss.hasError ? 'border border-borderError' : ''}
            hasError={stopLoss.hasError}
          />
          <ProfitLossField
            type="Loss"
            setPercentage={stopLoss.setCustomPercentage}
            setPrice={stopLoss.setPrice}
            position={position}
            product={product}
            pnlPercentage={stopLoss.pnlPercentage}
            hasError={stopLoss.hasError}
            customPercentage={stopLoss.customPercentage}
          />
        </div>
        <div className="h-8">
          <StopOrderMessage
            hasError={stopLoss.hasError}
            isInputFocused={stopLoss.isFocused}
            price={stopLoss.price}
            hasExistingOrder={stopLoss.hasExistingOrder}
            pnl={stopLoss.pnl}
            pnlPercentage={stopLoss.pnlPercentage}
            type="sl"
            priceDecimals={priceDecimals}
            stopLossPrice={stopLoss.price}
            takeProfitPrice={takeProfit.price}
            side={position.side === OrderSide.BUY ? 'long' : 'short'}
            productId={product.id}
            TpSlErrorComp={TpSlErrorComp}
          />
        </div>
      </Section>

      <div className="grid grid-cols-2 gap-2">
        <Button
          color="tertiary"
          onClick={() =>
            setPositionStopOrderModal({ positionId: undefined, openOrClosed: 'closed' })
          }
        >
          Cancel
        </Button>
        <Button
          disabled={
            takeProfit.hasError ||
            stopLoss.hasError ||
            (!takeProfit.price && !stopLoss.price && !hasStopOrder)
          }
          onClick={onSubmitClick}
          color="blue"
        >
          {hasStopOrder ? 'Modify' : 'Set'} TP/SL
        </Button>
      </div>
    </ResponsiveModal>
  );
};
